/*
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited 2023.
 */
import { FlowIfEnabled } from '@/components/blocks/FlexFlow';
import { ProgressIndicator } from '@/components/blocks/ProgressIndicator';
import { EMarketingSpot } from '@/components/content/EMarketingSpot';
import { FooterCardNavigation } from '@/components/content/Footer/parts/FooterCardNavigation/FooterCardNavigation';
import { GDPRDialog } from '@/components/content/Footer/parts/GDPRDialog';
import { LowerFooterSection } from '@/components/content/Footer/parts/LowerFooterSection/LowerFooterSection';
import { UpperFooterSection } from '@/components/content/Footer/parts/UpperFooterSection/UpperFooterSection';
import {
	footerAppMobileContainerSX,
	footerCustomContainerSX,
	footerMuiContainerSX,
} from '@/components/content/Footer/styles/container';
import { useModalPrivacyPolicy } from '@/data/Content/ModalPrivacyPolicy';
import { dFix, useSettings } from '@/data/Settings';
import { EMS_STORE_FEATURE } from '@/data/constants/flexFlowStoreFeature';
import type { ID } from '@/data/types/Basic';
import { mobileDisplaySx } from '@/styles/mobileApp';
import { Box, Container, Paper } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { usePathname } from 'next/navigation';

export const Footer = ({ id }: { id: ID; clickAction?: () => void }) => {
	const theme = useTheme();
	const pathname = usePathname();
	const { settings } = useSettings();
	const { data, loading, onSubmit, privacyNoticeVersion, privacyNoticeVersionValue } =
		useModalPrivacyPolicy();
	const acceptedSessionPrivacyPolicy =
		dFix(privacyNoticeVersion ?? '') === dFix(privacyNoticeVersionValue ?? '');
	const isMobileApp = settings.isMobileApp;
	const isCartPage = pathname?.startsWith('/cart');

	if (isMobileApp && isCartPage) {
		return <Box sx={footerAppMobileContainerSX} />;
	}

	return (
		<Paper
			component="footer"
			sx={{
				...footerCustomContainerSX(theme, settings?.csrSession),
				...mobileDisplaySx(isMobileApp),
			}}
			{...(settings?.csrSession && { 'data-iframe-height': '' })}
		>
			<Container sx={footerMuiContainerSX}>
				<UpperFooterSection emsName="MainFooter_SuperiorSection" />
				<FooterCardNavigation />
				<LowerFooterSection emsName="MainFooter_InferiorSection" />
				<EMarketingSpot id="MainFooter_Assets" properties={{ emsName: 'MainFooter_Assets' }} />
			</Container>

			{!acceptedSessionPrivacyPolicy ? (
				<FlowIfEnabled feature={EMS_STORE_FEATURE.CONSENT_OPTIONS}>
					{loading ? <ProgressIndicator /> : <GDPRDialog data={data} onSubmit={onSubmit} />}
				</FlowIfEnabled>
			) : null}
		</Paper>
	);
};
